/**
 * SFInfoCard — Salesforce-Rohdaten am Projekt (vom führenden Auftrag).
 * Quelle: §10.18-Followup — gleicher Inhalt in Project-Detail Übersicht-Tab
 * und in der Handover-Page-Karte.
 *
 * Felder werden aus `project.salesforce_data` gelesen, die Hono-API liefert
 * sie vom führenden Auftrag (`projectWithLeadSelect`).
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { formatIsoDate } from '../../lib/format-date.js';
import { Card } from './card.js';

type SfData = {
  projektstatus?: string | null | undefined;
  sparte?: string | null | undefined;
  abrechnungsart?: string | null | undefined;
  budget?: number | null | undefined;
  projektende?: string | null | undefined;
  projektstart?: string | null | undefined;
  projektnotizen?: string | null | undefined;
  opportunity?: { id: string; name: string } | null | undefined;
  qm_kontakt?: { id: string; name: string } | null | undefined;
  endkunde?: { id: string; name: string } | null | undefined;
};

function BlockShell({ embedded, children }: { embedded: boolean; children: ReactNode }) {
  return embedded ? <>{children}</> : <Card>{children}</Card>;
}

export function SFInfoCard({
  data,
  salesforceUrl,
  embedded = false,
}: {
  data: SfData | null | undefined;
  salesforceUrl?: string | null | undefined;
  embedded?: boolean;
}) {
  const { t, i18n } = useTranslation();
  const d = data ?? {};
  const fmtCurrency = new Intl.NumberFormat(i18n.language, {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });
  const budget = typeof d.budget === 'number' ? fmtCurrency.format(d.budget) : '—';
  const datum = (iso: string | null | undefined) =>
    iso ? formatIsoDate(iso, i18n.language) : null;

  // Dienstleistungsart (SF-Feld Sparte__c) ist inhaltlich identisch mit dem gleichnamigen
  // Schnellfakt — Feld hier raus, sonst doppelt. Beschriftung vereinheitlicht mit #80.
  // Spalten: Zeitraum | Status + Abrechnung | Opportunity + Beteiligte. Leere Felder fallen
  // innerhalb ihrer Spalte weg, wandern aber nie in eine andere.
  const fieldColumns = [
    [
      { label: t('v2.sfinfo.projektstart', 'Projektstart'), value: datum(d.projektstart) },
      { label: t('v2.sfinfo.projektende', 'Projektende'), value: datum(d.projektende) },
    ],
    [
      { label: t('v2.sfinfo.projektstatus', 'Projektstatus'), value: d.projektstatus },
      { label: t('v2.sfinfo.abrechnungsart', 'Abrechnungsart'), value: d.abrechnungsart },
    ],
    [
      { label: t('v2.sfinfo.opportunity', 'Opportunity'), value: d.opportunity?.name },
      { label: t('v2.sfinfo.endkunde', 'Endkunde'), value: d.endkunde?.name },
      { label: t('v2.sfinfo.qm_kontakt', 'QM-Kontakt'), value: d.qm_kontakt?.name },
    ],
  ].map((col) => col.filter((f) => f.value));

  return (
    <BlockShell embedded={embedded}>
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-sm font-medium" style={{ color: 'var(--ink)' }}>
          {t('v2.sfinfo.title', 'Salesforce-Daten')}
        </h3>
        {salesforceUrl && (
          <a
            href={salesforceUrl}
            target="_blank"
            rel="noreferrer"
            className="text-[11px]"
            style={{ color: 'var(--primary)', fontFamily: 'var(--f-mono)' }}
          >
            {t('v2.sfinfo.open_sf', 'SF öffnen ↗')}
          </a>
        )}
      </div>

      {/* embedded = Übergabekarte, dort steht Budget bereits in der Kopfzeile. */}
      {!embedded && (
        <div className="mb-4">
          <div
            className="text-[10px] uppercase tracking-[0.14em] mb-1"
            style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
          >
            {t('v2.sfinfo.budget', 'Budget')}
          </div>
          <div
            className="text-xl font-semibold tabular-nums"
            style={{ color: 'var(--ink)', fontFamily: 'var(--f-mono)' }}
          >
            {budget}
          </div>
        </div>
      )}

      {embedded ? (
        fieldColumns.some((col) => col.length > 0) && (
          // Drei feste Spalten statt fortlaufendem Raster (#83, Abnahme): sonst rutschen
          // Felder je nach Datenlage in wechselnde Zellen (Projektstart landete neben dem
          // Status, weil Endkunde/QM-Kontakt leer waren).
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mb-4">
            {fieldColumns.map((col, i) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: feste Spaltenreihenfolge
              <div key={i} className="space-y-2">
                {col.map((f) => (
                  <FieldTile key={f.label} label={f.label} value={f.value} />
                ))}
              </div>
            ))}
          </div>
        )
      ) : (
        <dl className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm mb-4">
          <FieldRow label={t('v2.sfinfo.projektstatus', 'Projektstatus')} value={d.projektstatus} />
          <FieldRow
            label={t('v2.sfinfo.abrechnungsart', 'Abrechnungsart')}
            value={d.abrechnungsart}
          />
          <FieldRow
            label={t('v2.sfinfo.projektstart', 'Projektstart')}
            value={datum(d.projektstart)}
          />
          <FieldRow
            label={t('v2.sfinfo.projektende', 'Projektende')}
            value={datum(d.projektende)}
          />
          <FieldRow label={t('v2.sfinfo.opportunity', 'Opportunity')} value={d.opportunity?.name} />
          <FieldRow label={t('v2.sfinfo.endkunde', 'Endkunde')} value={d.endkunde?.name} />
          <FieldRow label={t('v2.sfinfo.qm_kontakt', 'QM-Kontakt')} value={d.qm_kontakt?.name} />
        </dl>
      )}

      <ProjektnotizenBlock text={d.projektnotizen} />
    </BlockShell>
  );
}

/**
 * ProjektnotizenBlock — Überschrift + Text „Projektnotizen Operating", isoliert aus
 * `SFInfoCard`, weil derselbe rein lesende Block seit #119 auch in der KFML-Worklist
 * steht (`kfml-worklist.tsx`, `WorklistCard`). EINE Quelle statt zwei Kopien mit
 * eigenen i18n-Keys, die sonst auseinanderdriften (genau das war der Review-Befund).
 * Bewusst nur `text` als Prop, kein `SfData` — der Aufrufer in der Worklist hat kein
 * vollständiges `SfData`-Objekt.
 */
export function ProjektnotizenBlock({ text }: { text: string | null | undefined }) {
  const { t } = useTranslation();
  return (
    <div>
      <div
        className="text-[10px] uppercase tracking-[0.14em] mb-1"
        style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
      >
        {t('v2.sfinfo.projektnotizen', 'Projektnotizen Operating')}
      </div>
      {text ? (
        <p className="text-sm whitespace-pre-wrap" style={{ color: 'var(--ink-2)' }}>
          {text}
        </p>
      ) : (
        <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
          {t('v2.sfinfo.none', '—')}
        </p>
      )}
    </div>
  );
}

function FieldRow({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <>
      <dt
        className="text-[11px] uppercase tracking-[0.1em]"
        style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
      >
        {label}
      </dt>
      <dd style={{ color: value ? 'var(--ink)' : 'var(--ink-3)' }}>{value || '—'}</dd>
    </>
  );
}

function FieldTile({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="min-w-0">
      <div
        className="text-[10px] uppercase tracking-[0.1em] break-words"
        style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
      >
        {label}
      </div>
      <div className="text-sm min-w-0 break-words" style={{ color: 'var(--ink)' }}>
        {value}
      </div>
    </div>
  );
}
